import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Icon from '../../../../components/AppIcon';

const DetailedDayViewPanel = ({
  selectedDate,
  selectedDateDetails,
  selectedDayClockingsByType,
  getUiStatusBadgeClass,
  getUiStatusLabel,
} = {}) => {
  const { t } = useTranslation();
  const rtl = useSelector((state) => state.user.rtl);
  const titleIconGapClass = rtl ? 'ml-2 mr-0' : 'mr-2 ml-0';
  const inlineSpaceClass = rtl ? 'space-x-2 space-x-reverse' : 'space-x-2';
  return (
    <div className="bg-card border border-border rounded-xl">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground flex items-center">
          <Icon name="Eye" size={18} className={titleIconGapClass} />
          {t('Detailed Day View')}
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          {t('Click on a calendar date to view detailed attendance')}
        </p>
      </div>
      <div className="p-6">
        {selectedDate && selectedDateDetails ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-foreground">
                {new Date(selectedDate)?.toLocaleDateString(undefined, {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </h4>
              <div
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  getUiStatusBadgeClass?.(selectedDateDetails?.status)
                }`}
              >
                {getUiStatusLabel?.(selectedDateDetails?.status)}
              </div>
            </div>

            {selectedDateDetails?.status !== 'absent' ? (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <div className={`flex items-center ${inlineSpaceClass} mb-1`}>
                      <Icon name="LogIn" size={14} className="text-green-600" />
                      <span className="text-xs font-medium text-muted-foreground">{t('Clock In Time')}</span>
                    </div>
                    <div className="space-y-1">
                      {(selectedDayClockingsByType?.ClockIn || []).length > 0 ? (
                        (selectedDayClockingsByType.ClockIn || []).map((t, idx) => (
                          <div key={`clockin-${t}-${idx}`} className="text-lg font-semibold text-foreground">
                            {t}
                          </div>
                        ))
                      ) : (
                        <div className="text-lg font-semibold text-foreground">{t('N/A')}</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className={`flex items-center ${inlineSpaceClass} mb-1`}>
                      <Icon name="Coffee" size={14} className="text-orange-600" />
                      <span className="text-xs font-medium text-muted-foreground">{t('Break In Time')}</span>
                    </div>
                    <div className="space-y-1">
                      {(selectedDayClockingsByType?.BreakIn || []).length > 0 ? (
                        (selectedDayClockingsByType.BreakIn || []).map((t, idx) => (
                          <div key={`breakin-${t}-${idx}`} className="text-lg font-semibold text-foreground">
                            {t}
                          </div>
                        ))
                      ) : (
                        <div className="text-lg font-semibold text-foreground">{t('N/A')}</div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <div className={`flex items-center ${inlineSpaceClass} mb-1`}>
                      <Icon name="LogOut" size={14} className="text-red-600" />
                      <span className="text-xs font-medium text-muted-foreground">{t('Clock Out Time')}</span>
                    </div>
                    <div className="space-y-1">
                      {(selectedDayClockingsByType?.ClockOut || []).length > 0 ? (
                        (selectedDayClockingsByType.ClockOut || []).map((t, idx) => (
                          <div key={`clockout-${t}-${idx}`} className="text-lg font-semibold text-foreground">
                            {t}
                          </div>
                        ))
                      ) : (
                        <div className="text-lg font-semibold text-foreground">{t('N/A')}</div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className={`flex items-center ${inlineSpaceClass} mb-1`}>
                      <Icon name="Coffee" size={14} className="text-orange-600" />
                      <span className="text-xs font-medium text-muted-foreground">{t('Break Out Time')}</span>
                    </div>
                    <div className="space-y-1">
                      {(selectedDayClockingsByType?.BreakOut || []).length > 0 ? (
                        (selectedDayClockingsByType.BreakOut || []).map((t, idx) => (
                          <div key={`breakout-${t}-${idx}`} className="text-lg font-semibold text-foreground">
                            {t}
                          </div>
                        ))
                      ) : (
                        <div className="text-lg font-semibold text-foreground">{t('N/A')}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <Icon name="XCircle" size={32} className="mx-auto mb-2 text-red-500" />
                <p className="text-sm text-muted-foreground">{t('No attendance recorded')}</p>
                {selectedDateDetails?.reason && (
                  <p className="text-xs text-muted-foreground mt-1">{t('Reason')}: {selectedDateDetails?.reason}</p>
                )}
              </div>
            )}

            {selectedDateDetails?.hoursWorked > 0 && (
              <div className="pt-3 border-t border-border">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('Total Hours Worked')}</span>
                  <span className="text-lg font-semibold text-foreground">
                    {selectedDateDetails?.hoursWorked} {t('hours')}
                  </span>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Calendar" size={48} className="mx-auto mb-3 opacity-50" />
            <p>{t('Select a date from the calendar to view details')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DetailedDayViewPanel;
